import { createModel } from '@rematch/core';
import { CheckInQuestion } from '../../models/check-in-question';
import { getCheckInQuestions } from '../../apis/check-in-question';

interface ProcessData {
    byAppointmentId: Dictionary<string[]>;
    byId: Dictionary<CheckInQuestion>;
}

export interface State {
    byAppointmentId: Dictionary<string[]>;
    byId: Dictionary<CheckInQuestion>;
    loading: Dictionary<boolean>;
}

export const checkInQuestions = createModel<State>({
    state: {
        byAppointmentId: {},
        byId: {},
        loading: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<MixType<CheckInQuestion>>,
            loadingKey?: string
        ): State => {
            const isUpdated = checkIsUpdate(state.byId, data);
            const processedData = processData(state.byAppointmentId, data, isUpdated);
            return {
                ...state,
                byAppointmentId: {
                    ...state.byAppointmentId,
                    ...processedData.byAppointmentId
                },
                byId: {
                    ...state.byId,
                    ...processedData.byId
                },
                ...loadingKey && {
                    loading: {
                        ...state.loading,
                        [loadingKey]: false
                    }
                }
            };
        },
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        })
    },
    effects: () => ({
        async getCheckInQuestionsAsync(appointmentId: number) {
            this.loading('getCheckInQuestionsAsync', true);
            try {
                const data = await getCheckInQuestions(appointmentId);
                this.update(data, 'getCheckInQuestionsAsync');
            } catch (e) {
                window.console.log(e);
                this.loading('getCheckInQuestionsAsync', false);
            }
        }
    })
});

export const checkIsUpdate = (
    byId: Dictionary<CheckInQuestion>,
    data: Undefinable<MixType<CheckInQuestion>>
): boolean => {
    if (data) {
        if (Array.isArray(data)) {
            return data.every(x => byId[x.id]);
        } else {
            return !!byId[data.id];
        }
    } else {
        return true;
    }
};

export const processData = (
    initialByAppointmentId: Dictionary<string[]>,
    data: Undefinable<MixType<CheckInQuestion>>,
    isUpdated: boolean
): ProcessData => {
    const byAppointmentId = {} as Dictionary<string[]>;
    const byId = {} as Dictionary<CheckInQuestion>;
    if (data) {
        if (Array.isArray(data)) {
            data.sort((a, b) => a.questionDate > b.questionDate ? 1 : -1);
            for (let i = 0; i < data.length; i++) {
                byId[data[i].id] = data[i];
                if (!isUpdated) {
                    if (!byAppointmentId[data[i].appointmentId]) {
                        byAppointmentId[data[i].appointmentId] = initialByAppointmentId[data[i].appointmentId] || [];
                    }
                    byAppointmentId[data[i].appointmentId]!.push(data[i].id);
                }
            }
        } else {
            byId[data.id] = data;
            if (!isUpdated) {
                byAppointmentId[data.appointmentId] = [...initialByAppointmentId[data.appointmentId] || [], data.id];
            }
        }
    }
    return {
        byAppointmentId,
        byId
    };
};
